import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchAccounts, fetchCategories, fetchTags, getAccounts, groupMovements, getAccountBalanceHistory, getInvestmentRoi } from '../api/endpointsBackend';
import {
  getRangeWindow,
  getRangeDateWindow,
  getMonthsInWindow,
  getPeriodsInWindow,
  granularityForRange,
  granularityForWindow,
  toISODate,
  snapToPeriod,
  bucketInWindow,
  computeDeltaPct,
  buildBalanceSeries,
  getAvailableCurrencies,
} from '../utils/movementBuckets';
import { isDebtAccount, getDisplayCategory, CATEGORY_ORDER } from '../utils/accountTypes';
import { useDisplayCurrency } from '../contexts/DisplayCurrencyContext';
import { useUsdRates } from './useUsdRates';
import { toUnconvertibleBuckets } from '../utils/unconvertibleReason';
import { holdsForeign } from '../utils/rateSources';

// Pull all history so the cumulative net (patrimonio) starts at the user's
// earliest movement, whatever year that is: the requests carry no start date,
// because any fixed one would leave out the movements dated before it and
// every balance would read short. Backend handles this fine — payload is
// small for personal-finance scale.
function buildTimelineEnd(now) {
  return `${now.getFullYear()}-12-31`;
}

/**
 * Per-account row stats from a cumulative balance series. Debt accounts are
 * abs'd so a row reads "you owe X" as a positive number; delta polarity is
 * derived from the signed direction, and a near-zero baseline suppresses the
 * pct (a tiny previous balance yields astronomical percentages). Shared by the
 * single-currency view, the per-currency ("Todas") view and the converted view
 * so a balance/delta means the same thing wherever it renders.
 */
function statsFromSeries(rawSeries, isDebt) {
  const series = isDebt ? rawSeries.map(v => Math.abs(v)) : rawSeries;
  const last = series[series.length - 1] ?? 0;
  const first = series[0] ?? 0;
  const baselineUsable = Math.abs(first) > 1;
  const direction = last - first;
  const isNeutral = Math.abs(direction) < 0.005;
  return {
    balance: last,
    prevBalance: first,
    sparkline: series,
    direction,
    delta_pct: baselineUsable ? computeDeltaPct(last, first) : null,
    delta_is_good: isNeutral ? null : (isDebt ? direction < 0 : direction > 0),
  };
}

/**
 * Feeds the Accounts overview page.
 *
 * Makes exactly 2 requests on mount (mirrors the Categories pattern):
 *   1. GET /accounts (active only)
 *   2. POST /movements/group with dimensions=['year','month','currency','type','account_id']
 *      over the full history so range changes don't refetch.
 *
 * Range / currency are pure derivations on the cached buckets — no extra network.
 *
 * Top card semantics:
 *   - Assets = Σ current balance across non-debt accounts (savings + investment +
 *     other), in the selected currency. Same source as the per-row balances and
 *     group footers, so the header reconciles with the rows below.
 *   - Debt   = Σ |current balance| across debt accounts (computed in `debtSummary`).
 *   - Δ Assets / Δ Debt = growth vs end of previous comparable period, derived
 *     from per-account `prevBalance` so deltas line up with the row sparklines.
 */
export function useAccountsOverview({ initialRange = '1M', accountDetails = true, period = null } = {}) {
  // A caller that opens on another range names it here, so the first fetch is
  // already for that range. One that shows no account's reconciliation status
  // or position (the Dashboard's net-worth card) passes `accountDetails: false`
  // and reads the account list the rest of the app shares. One with a period
  // of its own (the Dashboard's date filter, MI-240) passes it as `period`
  // ({ startDate, endDate }, no start for all time): it replaces the range
  // pills, and the series end on its last day, never after today.
  const [range, setRange] = useState(initialRange);
  const [currency, setCurrency] = useState(null);  // null = pick first available
  const [accountList, setAccounts] = useState([]);
  // MI-324: account id → reconciliation status. It arrives after the list, so
  // it is kept apart and joined onto each account once it does.
  const [reconciliation, setReconciliation] = useState(null);
  const fetchRun = useRef(0);   // the latest fetch started
  const shownRun = useRef(0);   // the fetch whose reconciliation is on screen
  const accounts = useMemo(
    () => (reconciliation
      ? accountList.map(a => (reconciliation.has(a.id) ? { ...a, reconciliation: reconciliation.get(a.id) } : a))
      : accountList),
    [accountList, reconciliation]
  );
  const [buckets, setBuckets] = useState([]);
  const [balanceBuckets, setBalanceBuckets] = useState([]);
  const [roiRows, setRoiRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // MI-307 fine-grained payload for the current range (null while the range is
  // monthly, or until its fetch resolves). Cached per range so re-selecting a
  // pill costs nothing.
  const [fine, setFine] = useState(null);
  const [fineLoading, setFineLoading] = useState(false);
  const fineCache = useRef(new Map());

  const fetchData = useCallback(async () => {
    const run = ++fetchRun.current;
    setLoading(true);
    setError(null);
    // The Accounts list is the one surface that renders the reconciliation
    // badge, so it's the one caller that opts into the cost (MI-311). It is the
    // slowest read by far, so the page does not wait for it: each row's badge
    // appears when it arrives (MI-324). A refetch keeps the badges it has until
    // an answer at least as new replaces them; an older one arriving late is
    // dropped.
    if (accountDetails) {
      fetchAccounts(null, { includeReconciliation: true })
        .then((res) => {
          if (run >= shownRun.current) {
            shownRun.current = run;
            setReconciliation(new Map((res || []).map(a => [a.id, a.reconciliation])));
          }
        })
        .catch(err => console.error('Failed to fetch account reconciliation:', err));
    }
    try {
      const now = new Date();
      const end = buildTimelineEnd(now);

      const [accountsRes, bucketsRes, balanceBucketsRes, roiRes, categoriesRes, tagsRes] = await Promise.all([
        accountDetails
          ? fetchAccounts(null, { includePositions: true })
          : getAccounts(),
        groupMovements(
          [],
          ['year', 'month', 'currency', 'type', 'account_id'],
          null,
          end,
        ),
        getAccountBalanceHistory(null, end),
        getInvestmentRoi(),
        fetchCategories(),
        fetchTags(),
      ]);

      setAccounts(accountsRes || []);
      setBuckets(bucketsRes || []);
      setBalanceBuckets(balanceBucketsRes || []);
      setRoiRows(roiRes || []);
      setCategories(categoriesRes || []);
      setAllTags(tagsRes || []);
    } catch (err) {
      console.error('Failed to fetch accounts overview:', err);
      setError('Failed to fetch accounts overview');
    } finally {
      setLoading(false);
    }
  }, [accountDetails]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasPeriod = Boolean(period);
  const periodStart = period?.startDate || null;
  const periodEndDate = period?.endDate || null;
  const dateWindow = useMemo(() => {
    if (!hasPeriod) return getRangeDateWindow(range);
    const today = toISODate(new Date());
    return { startDate: periodStart, endDate: !periodEndDate || periodEndDate > today ? today : periodEndDate };
  }, [hasPeriod, range, periodStart, periodEndDate]);
  const granularity = hasPeriod
    ? granularityForWindow(dateWindow.startDate, dateWindow.endDate)
    : granularityForRange(range);
  // Fine slices are cached per window, so re-selecting a pill or a period costs nothing.
  const fineKey = `${dateWindow.startDate}|${dateWindow.endDate}|${granularity}`;
  // The trend chart's Assets line is cumulative, so its fine series needs a
  // starting value. Fetching from the first of the window's opening month lets
  // it anchor on the cached monthly cumulative (through the prior month) and
  // then run forward at the fine grain — exact, with no extra request.
  const trendFetchStart = useMemo(
    () => (dateWindow.startDate ? snapToPeriod(dateWindow.startDate, 'month') : null),
    [dateWindow.startDate]
  );

  // Fine-grained fetch for the short ranges. 1Y / ALL never enter here — they
  // keep deriving from the monthly payload already in memory (zero network).
  useEffect(() => {
    if (granularity === 'month' || !dateWindow.startDate) { setFine(null); return; }
    const cached = fineCache.current.get(fineKey);
    if (cached) { setFine(cached); return; }

    let cancelled = false;
    setFineLoading(true);
    Promise.all([
      getAccountBalanceHistory(dateWindow.startDate, dateWindow.endDate, null, {
        granularity,
        includeWindowOpening: true,
      }),
      groupMovements(
        [],
        ['period', 'currency', 'type', 'account_id'],
        trendFetchStart,
        dateWindow.endDate,
        [],
        'and',
        granularity,
      ),
    ])
      .then(([balanceRes, groupRes]) => {
        const payload = {
          key: fineKey,
          granularity,
          balanceBuckets: balanceRes || [],
          groupBuckets: groupRes || [],
        };
        fineCache.current.set(fineKey, payload);
        if (!cancelled) setFine(payload);
      })
      .catch(err => {
        // Fall back to the monthly derivation rather than blanking the page.
        console.error('Failed to fetch fine-grained balance history:', err);
        if (!cancelled) setFine(null);
      })
      .finally(() => { if (!cancelled) setFineLoading(false); });

    return () => { cancelled = true; };
  }, [fineKey, granularity, dateWindow.startDate, dateWindow.endDate, trendFetchStart]);

  // Refetching the base payload invalidates the fine slices derived alongside it.
  useEffect(() => { fineCache.current.clear(); }, [balanceBuckets]);

  const availableCurrencies = useMemo(() => getAvailableCurrencies(buckets), [buckets]);

  const effectiveCurrency = useMemo(() => {
    if (currency) return currency;
    return availableCurrencies[0] || 'COP';
  }, [currency, availableCurrencies]);

  // Display-currency conversion (MI-298). Read here (not lower down) so the
  // trend chart can convert too — it must reconcile with the header cards.
  const { displayCurrency } = useDisplayCurrency();
  const { rates: usdRates, sources: rateSources, currencies: currencyDefs } = useUsdRates();

  // A period's month window is the months its dates fall in (all time: none to start).
  const window = useMemo(() => {
    if (!hasPeriod) return getRangeWindow(range);
    const ym = (d) => ({ year: Number(d.slice(0, 4)), month: Number(d.slice(5, 7)) });
    return { start: dateWindow.startDate ? ym(dateWindow.startDate) : null, end: ym(dateWindow.endDate) };
  }, [hasPeriod, range, dateWindow]);

  // Full month grid covering the entire payload — anchors cumulative series
  const allMonths = useMemo(() => {
    const now = new Date();
    return getMonthsInWindow(null, { year: now.getFullYear(), month: now.getMonth() + 1 }, buckets);
  }, [buckets]);

  // Sparkline window for the monthly ranges. MI-307 removed the one-month
  // backward extension that used to sit here: it existed only so 1W/1M had ≥ 2
  // points, and it made every range's Δ% measure one period more than its label
  // promised. The baseline is now the first point *inside* the window, for
  // every range — at fine grain that point is the backend's opening anchor.
  const accountMonths = useMemo(() => {
    if (!window.start || !window.end) return allMonths;
    return getMonthsInWindow(window.start, window.end, []);
  }, [window, allMonths]);

  // One period axis + one bucket source, whichever grain is active. Everything
  // downstream (rows, group footers, header cards, converted view) reads these
  // so a balance means the same thing wherever it renders.
  const fineActive = Boolean(fine && fine.key === fineKey);
  const activeGranularity = fineActive ? fine.granularity : 'month';
  const seriesBuckets = fineActive ? fine.balanceBuckets : balanceBuckets;
  const seriesPeriods = useMemo(() => {
    if (fineActive) {
      return getPeriodsInWindow(dateWindow.startDate, dateWindow.endDate, fine.granularity);
    }
    return accountMonths.map(m => snapToPeriod(`${m.year}-${String(m.month).padStart(2, '0')}-01`, 'month'));
  }, [fineActive, fine, dateWindow, accountMonths]);

  const currentBuckets = useMemo(
    () => buckets.filter(b => bucketInWindow(b, window.start, window.end)),
    [buckets, window]
  );

  const months = useMemo(
    () => getMonthsInWindow(window.start, window.end, currentBuckets),
    [window, currentBuckets]
  );

  // Per-account row stats — same semantics as the panel chart so a Debit
  // account's row balance matches the chart's value: cumulative signed balance
  // sampled at each month in the overview range. For Credit/Loan accounts we
  // flip the sign so the row reads "you owe X" as a positive number.
  //
  // Sparkline covers prevEnd → curEnd so the delta always has a meaningful baseline.
  const accountStats = useMemo(() => {
    const stats = new Map();
    accounts.forEach(a => {
      const isDebt = isDebtAccount(a.account_group, a.account_type);
      const raw = buildBalanceSeries(seriesBuckets, a.id, seriesPeriods, effectiveCurrency, activeGranularity);
      stats.set(a.id, statsFromSeries(raw, isDebt));
    });
    return stats;
  }, [accounts, seriesBuckets, seriesPeriods, activeGranularity, effectiveCurrency]);

  // "Todas" (multi-currency) view: a row can hold several currencies, so instead
  // of one balance forced into COP, each account carries a line per currency it
  // actually holds — each with its own sparkline/delta from that currency's own
  // series. Empty currencies are dropped so a COP-only account shows one line.
  const perCurrencyAccountStats = useMemo(() => {
    const map = new Map(); // account_id -> [{ currency, ...stats }]
    accounts.forEach(a => {
      const isDebt = isDebtAccount(a.account_group, a.account_type);
      const lines = [];
      availableCurrencies.forEach(cur => {
        const raw = buildBalanceSeries(seriesBuckets, a.id, seriesPeriods, cur, activeGranularity);
        const last = raw[raw.length - 1] ?? 0;
        const first = raw[0] ?? 0;
        if (last === 0 && first === 0) return; // no holdings in this currency
        lines.push({ currency: cur, ...statsFromSeries(raw, isDebt) });
      });
      map.set(a.id, lines);
    });
    return map;
  }, [accounts, seriesBuckets, seriesPeriods, activeGranularity, availableCurrencies]);

  // Header totals derive from the same per-account balances rendered in the
  // rows below, restricted to active accounts so the header always equals the
  // sum of the (default) group footers. Closed accounts often have stale
  // balances and would otherwise inflate the headline number.
  const activeAccounts = useMemo(
    () => accounts.filter(a => a.is_active !== false),
    [accounts]
  );

  const debtSummary = useMemo(() => {
    const debtAccounts = activeAccounts.filter(a => isDebtAccount(a.account_group, a.account_type));
    const cur = debtAccounts.reduce((s, a) => s + (accountStats.get(a.id)?.balance     || 0), 0);
    const prv = debtAccounts.reduce((s, a) => s + (accountStats.get(a.id)?.prevBalance || 0), 0);
    return { total: cur, delta_pct: computeDeltaPct(cur, prv) };
  }, [activeAccounts, accountStats]);

  const summary = useMemo(() => {
    const assetAccounts = activeAccounts.filter(a => !isDebtAccount(a.account_group, a.account_type));
    const cur = assetAccounts.reduce((s, a) => s + (accountStats.get(a.id)?.balance     || 0), 0);
    const prv = assetAccounts.reduce((s, a) => s + (accountStats.get(a.id)?.prevBalance || 0), 0);
    return { assets: { total: cur, delta_pct: computeDeltaPct(cur, prv) } };
  }, [activeAccounts, accountStats]);

  // MI-298 stock view: when a display currency is picked, value every native
  // balance at TODAY's rate (usd_per_unit from GET /currencies) and sum across
  // currencies. Same windowing/series as the per-currency cards; the delta uses
  // today's rate on both endpoints so it measures holdings growth, not FX noise.
  //
  // Built as per-account stats (same shape as accountStats) so rows, group
  // footers and the header cards all derive from ONE dataset and reconcile.
  // (displayCurrency / usdRates / currencyDefs are read up top so the trend
  // chart can convert too.)
  const convertedData = useMemo(() => {
    if (!displayCurrency || !usdRates) return null;
    const target = usdRates[displayCurrency];
    if (!target) return null; // target itself unconvertible — keep native view
    // Respect the currency tab: converting with a specific currency selected means
    // "show only that currency, expressed in the display currency" (e.g. USD→COP),
    // not "everything → COP". `currency` is null for "Todas" (convert everything).
    const allCurrencies = [...new Set(balanceBuckets.map(b => b.currency).filter(Boolean))];
    const balanceCurrencies = currency ? allCurrencies.filter(c => c === currency) : allCurrencies;
    const stats = new Map();
    let numUnconvertible = 0;
    // Tallied per currency so the disclosure can name it and say why.
    const unconvertibleByCurrency = new Map();
    accounts.forEach(a => {
      const isDebt = isDebtAccount(a.account_group, a.account_type);
      let combined = null; // summed converted series across this account's currencies
      balanceCurrencies.forEach(cur => {
        const raw = buildBalanceSeries(seriesBuckets, a.id, seriesPeriods, cur, activeGranularity);
        const perCur = isDebt ? raw.map(v => Math.abs(v)) : raw; // match accountStats
        const last = perCur[perCur.length - 1] ?? 0;
        const first = perCur[0] ?? 0;
        if (last === 0 && first === 0) return; // no holdings in this currency
        const upu = usdRates[cur];
        if (upu == null) { // disclosed, never silent (active accounts only)
          if (a.is_active !== false) {
            numUnconvertible += 1;
            unconvertibleByCurrency.set(cur, (unconvertibleByCurrency.get(cur) || 0) + 1);
          }
          return;
        }
        const factor = upu / target;
        if (!combined) combined = perCur.map(v => v * factor);
        else perCur.forEach((v, i) => { combined[i] = (combined[i] || 0) + v * factor; });
      });
      const series = combined || [];
      const last = series[series.length - 1] ?? 0;
      const first = series[0] ?? 0;
      const baselineUsable = Math.abs(first) > 1;
      const direction = last - first;
      const isNeutral = Math.abs(direction) < 0.005;
      stats.set(a.id, {
        balance: last,
        prevBalance: first,
        sparkline: series,
        direction,
        delta_pct: baselineUsable ? computeDeltaPct(last, first) : null,
        delta_is_good: isNeutral ? null : (isDebt ? direction < 0 : direction > 0),
      });
    });
    return {
      stats,
      numUnconvertible,
      unconvertible: toUnconvertibleBuckets(unconvertibleByCurrency, currencyDefs),
    };
  }, [displayCurrency, usdRates, currencyDefs, accounts, balanceBuckets, seriesBuckets, seriesPeriods, activeGranularity, currency]);

  const convertedAccountStats = convertedData?.stats || null;

  // MI-329: an overview figure is marked "≈" only when one of its open accounts
  // holds another currency, as on the phone; one of pesos shown in pesos is exact.
  const convertedIsConverted = useMemo(() => {
    const anyForeign = (list) => Boolean(convertedAccountStats) && list.some((a) => a.is_active !== false
      && holdsForeign((perCurrencyAccountStats.get(a.id) || []).map((l) => l.currency), displayCurrency));
    const all = accounts || [];
    return {
      net: anyForeign(all),
      assets: anyForeign(all.filter((a) => !isDebtAccount(a.account_group, a.account_type))),
      debt: anyForeign(all.filter((a) => isDebtAccount(a.account_group, a.account_type))),
    };
  }, [accounts, perCurrencyAccountStats, convertedAccountStats, displayCurrency]);

  // Trend chart series — assets and debt as balances over time.
  //
  // These are summed from the very same per-account balance series the header
  // cards total, so the last point of each line IS the headline figure. The
  // chart used to plot cumulative net worth against monthly expense flow under
  // "Assets"/"Debt" labels: two different quantities, neither of which was the
  // number printed above it, which is why hovering the chart could never be
  // made to read out anything meaningful.
  //
  // Active accounts only, like the header (MI-227, MI-312): a closed account's
  // stale balance would leave each line ending away from its headline figure.
  // `net` (assets − debt) is the line "Combine assets and debt" plots.
  const balanceTrend = useMemo(() => {
    const stats = convertedAccountStats || accountStats;
    const len = seriesPeriods.length;
    const assets = new Array(len).fill(0);
    const debt = new Array(len).fill(0);
    activeAccounts.forEach(a => {
      const s = stats?.get(a.id);
      if (!s || !s.sparkline || !s.sparkline.length) return;
      // debt series are already absolute (statsFromSeries), so this sums to the
      // positive "you owe X" figure the Deuda card shows
      const into = isDebtAccount(a.account_group, a.account_type) ? debt : assets;
      s.sparkline.forEach((v, i) => { if (i < len) into[i] += v || 0; });
    });
    return { assets, expenses: debt, net: assets.map((v, i) => v - debt[i]) };
  }, [activeAccounts, accountStats, convertedAccountStats, seriesPeriods]);

  // Header cards derive from the same converted per-account stats the rows
  // render, restricted to active accounts (mirrors `summary`/`debtSummary`).
  const convertedSummary = useMemo(() => {
    if (!convertedData) return null;
    const { stats, numUnconvertible, unconvertible } = convertedData;
    const sum = (accs, key) => accs.reduce((s, a) => s + (stats.get(a.id)?.[key] || 0), 0);
    const assetAccounts = activeAccounts.filter(a => !isDebtAccount(a.account_group, a.account_type));
    const debtAccounts = activeAccounts.filter(a => isDebtAccount(a.account_group, a.account_type));
    const assets = sum(assetAccounts, 'balance');
    const prevAssets = sum(assetAccounts, 'prevBalance');
    const debt = sum(debtAccounts, 'balance'); // per-account series already abs'd
    const prevDebt = sum(debtAccounts, 'prevBalance');
    return {
      assets: { total: assets, delta_pct: computeDeltaPct(assets, prevAssets) },
      debt:   { total: debt,   delta_pct: computeDeltaPct(debt, prevDebt) },
      num_unconvertible: numUnconvertible,
      unconvertible,
    };
  }, [convertedData, activeAccounts]);


  // Map of account_id → ROI row in the selected currency. Investment accounts
  // can hold multiple currencies; we pick the row matching the displayed
  // currency, falling back to the first row if the displayed currency isn't
  // available for that account.
  const roiByAccount = useMemo(() => {
    const map = new Map();
    roiRows.forEach(r => {
      const existing = map.get(r.account_id);
      if (!existing) {
        map.set(r.account_id, r);
      } else if (r.currency === effectiveCurrency && existing.currency !== effectiveCurrency) {
        map.set(r.account_id, r);
      }
    });
    return map;
  }, [roiRows, effectiveCurrency]);

  // MI-298: ROI rows show net P/L (gains − losses) in the ROI row's own
  // currency; in converted mode scale those magnitudes to the display currency
  // too. Percentages are unit-less and stay as-is.
  const convertedRoiByAccount = useMemo(() => {
    if (!convertedData) return null;
    const target = usdRates[displayCurrency];
    const map = new Map();
    roiByAccount.forEach((r, id) => {
      const upu = usdRates[r.currency];
      if (upu == null || !target) { map.set(id, r); return; } // keep native
      const factor = upu / target;
      map.set(id, { ...r, gains: (r.gains || 0) * factor, losses: (r.losses || 0) * factor });
    });
    return map;
  }, [convertedData, usdRates, displayCurrency, roiByAccount]);

  const accountsByGroup = useMemo(() => {
    const groups = {};
    accounts.forEach(a => {
      const g = getDisplayCategory(a);
      if (!groups[g]) groups[g] = [];
      groups[g].push(a);
    });
    // Sort within each group by institution, then account name (MI-304), so
    // every account from the same bank sits together. Replaces a
    // balance-descending sort: grouping by institution matches how people scan
    // the list ("what do I have at Bancolombia?") better than magnitude does.
    //
    // Compares the source NAME rather than source_id — it is what the user
    // reads, and the picker keeps the string in step with the entity. A legacy
    // account still holding a different spelling of the same institution will
    // therefore sort apart until that data is cleaned up.
    const byName = (x, y) =>
      (x || '').localeCompare(y || '', undefined, { sensitivity: 'base' });
    Object.values(groups).forEach(list => {
      list.sort((a, b) => {
        const sa = (a.source || '').trim();
        const sb = (b.source || '').trim();
        // Accounts with no institution go last — cash, money lent to people.
        // Their own name is the only identity they have.
        if (!sa !== !sb) return sa ? -1 : 1;
        return byName(sa, sb) || byName(a.name, b.name);
      });
    });
    // Return groups in canonical display order
    const ordered = {};
    CATEGORY_ORDER.forEach(cat => { if (groups[cat]) ordered[cat] = groups[cat]; });
    return ordered;
  }, [accounts]);

  return {
    loading,
    fineLoading,
    error,
    refetch: fetchData,
    range, setRange,
    granularity: activeGranularity,
    chartPeriods: seriesPeriods,
    currency: effectiveCurrency,
    setCurrency,
    availableCurrencies,
    accounts,
    activeAccounts,
    accountsByGroup,
    accountStats,
    perCurrencyAccountStats,
    roiByAccount,
    summary,
    debtSummary,
    convertedSummary,
    convertedAccountStats,
    convertedIsConverted,
    convertedRoiByAccount,
    displayCurrency,
    usdRates,
    rateSources,
    trend: balanceTrend,
    months,
    categories,
    allTags,
    buckets,
    balanceBuckets,
  };
}
